<template>
  <v-card rounded="lg">
    <v-container fluid>
      <v-row dense align="center">
        <v-col cols="auto">
          <PatientAvatar :attendance="attendance" />
        </v-col>

        <v-col cols="3">
          <v-row dense align="center">
            <v-col cols="auto"> #{{ attendance.patient.warelineId }} </v-col>
            <v-col cols="auto">
              <PatientRisk
                :attendance="attendance"
                :attendanceEsigned="attendanceEsigned"
                defaultColor="black"
              />
            </v-col>
          </v-row>

          <v-row dense align="center">
            <v-col class="text-h5 gray--text">
              {{ attendance.patient.name }}
            </v-col>
          </v-row>

          <v-row dense align="center">
            <v-col>
              {{ attendance.patient.birthDate | moment("DD/MM/Y") }} -
              {{ attendance.healthInsuranceProductName }}
            </v-col>
          </v-row>

          <v-row dense align="center">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-col
                  cols="auto"
                  v-bind="attrs"
                  v-on="on"
                  :class="[attendance.fittedSchedule ? 'orange--text' : '']"
                >
                  <v-icon
                    small
                    :color="attendance.fittedSchedule ? 'orange' : ''"
                  >
                    ri-calendar-line
                  </v-icon>

                  {{
                    $moment(attendance.scheduledFor, "YYYY-MM-DD HH:mm:ss ZZ")
                      | moment("HH:mm")
                  }}
                </v-col>
              </template>

              <span>
                Horário de agendamento
                <span v-if="attendance.fittedSchedule">(encaixe)</span>
              </span>
            </v-tooltip>

            <v-tooltip top v-if="attendance.arrivedAt">
              <template v-slot:activator="{ on, attrs }">
                <v-col cols="auto" v-bind="attrs" v-on="on">
                  <v-icon small>eva-clock-outline</v-icon>

                  {{
                    $moment(attendance.arrivedAt, "YYYY-MM-DD HH:mm:ss ZZ")
                      | moment("HH:mm")
                  }}
                </v-col>
              </template>

              <span>Horário de chegada</span>
            </v-tooltip>
          </v-row>
        </v-col>

        <v-col cols="3">
          <v-row dense align="center">
            <v-col class="font-weight-bold gray--text">
              #{{ attendance.warelineId }}
            </v-col>
          </v-row>

          <v-row dense align="center">
            <v-col class="font-weight-bold gray--text">
              {{ attendance.employee.name }}
            </v-col>
          </v-row>

          <v-row dense align="center">
            <v-col> {{ islandName }} </v-col>
          </v-row>

          <v-row dense align="center">
            <v-col> {{ attendance.serviceType.name }} </v-col>
          </v-row>
        </v-col>

        <v-col cols="auto">
          <v-row>
            <PatientNotes
              :attendanceId="attendance.id"
              :notes="attendance.notes"
            />
          </v-row>
          <v-row dense justify="center" align="center">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  @click="expandTherapyMenu = !expandTherapyMenu"
                >
                  <v-icon>
                    eva-droplet-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>Aplicar Terapêutica</span>
            </v-tooltip>
            <v-expand-x-transition>
              <v-col cols="auto" v-show="this.expandTherapyMenu">
                <AttendanceTherapyProtocolForm
                  class="mr-4"
                  :attendance="attendance"
                  :attendanceEsigned="attendanceEsigned"
                />
              </v-col>
            </v-expand-x-transition>
          </v-row>
        </v-col>

        <v-col cols="auto">
          <v-row>
            <v-tooltip top v-if="attendance.arrivedAt">
              <template v-slot:activator="{ on, attrs }">
                <v-col cols="auto" v-bind="attrs" v-on="on">
                  <v-icon small>eva-clock-outline</v-icon>
                  {{ StatusTime }}
                </v-col>
              </template>

              <span>Tempo nesta etapa do atendimento</span>
            </v-tooltip>
          </v-row>
          <v-row>
            <WorkflowableStatus :workflowable="attendance" />
          </v-row>
        </v-col>

        <v-spacer />

        <v-col cols="auto">
          <authorization
            permissionKey="move_attendance_on_workflow"
            can="action"
            v-slot:default="{ authorization }"
          >
            <Actions
              v-if="authorization"
              :attendanceId="attendance.id"
              :attendance="attendance"
              :currentWorkflowStatus="currentWorkflowStatus"
              :permittedWorkflowStatuses="attendance.permittedWorkflowStatuses"
              :refreshQueue="refreshQueue"
              :attendanceEsigned="attendanceEsigned"
              :medicalRecordPath="medicalRecordPath"
            />
          </authorization>
        </v-col>
      </v-row>

      <slot name="details" :attendance="attendance"> </slot>
    </v-container>
  </v-card>
</template>

<script>
import AttendanceTherapyProtocolForm from "@/components/attendance/therapy_protocol_form/index.vue";
import PatientAvatar from "@/components/queues/attendance/patient_avatar/index.vue";
import PatientNotes from "@/components/queues/attendance/patient_notes.vue";
import PatientRisk from "@/components/queues/attendance/patient_risk/index.vue";
import WorkflowableStatus from "@/components/workflowable/status.vue";
import Actions from "@/components/queues/attendance/actions/index.vue";
import { buildSubscription } from "@/components/graphql/subscriptions.js";
import NOTES_UPDATED from "@/graphql/subscriptions/attendance/notes_updated.gql";

export default {
  name: "Attendance",
  components: {
    AttendanceTherapyProtocolForm,
    PatientAvatar,
    PatientNotes,
    PatientRisk,
    WorkflowableStatus,
    Actions,
  },
  props: {
    attendance: Object,
    medicalRecordPath: String,
    refreshQueue: {
      type: Function,
      default: () => {},
    },
  },
  apollo: {
    $subscribe: {
      attendanceNotesUpdated() {
        return buildSubscription({
          query: NOTES_UPDATED,
          variables: {
            attendanceId: this.attendance.id,
          },
          onSuccess: ({ attendanceNotesUpdated: { notes, attendanceId } }) => {
            if (attendanceId !== this.attendance.id) return;
            this.attendance.notes = notes;
          },
          onSubscriptionLoadStatusUpdate: (value) => {
            this.subscriptionLoaded = value;
          },
          errorNotificationMessage:
            "Ocorreu um erro ao tentar atualizar as notas de paciente.\n" +
            "Você não está recebendo mais atualizações, recarregue a página para atualizar.",
        });
      },
    },
  },
  data() {
    return {
      hideFittedScheduleBadge: true,
      lastWorkflowLog: null,
      expandTherapyMenu: false,
    };
  },
  watch: {
    attendance: {
      deep: true,
      immediate: true,
      handler(attendance) {
        this.lastWorkflowLog = attendance.lastWorkflowLog;
      },
    },
  },
  computed: {
    StatusTime() {
      return `Chegou há ${this.$moment(
        this.attendance.lastWorkflowLog.createdAt,
        "YYYY-MM-DD HH:mm:ss ZZ"
      )
        .startOf()
        .fromNow("hour")}`;
    },
    currentWorkflowStatus() {
      if (this.lastWorkflowLog) {
        return this.lastWorkflowLog.toStatus;
      }

      return null;
    },
    islandName() {
      if (this.attendance?.employeeAttendancePlaces) {
        const employeeAttendancePlaces = this.attendance.employeeAttendancePlaces.map(
          (e) => e.parent.name
        );

        return this.lodash.uniq(employeeAttendancePlaces).join(", ");
      }

      return "Sem ilha";
    },
    attendanceEsigned() {
      return this.attendance.lastEsign.status === "signed";
    },
  },
};
</script>

<style lang="scss" scoped>
.attendance {
  p {
    margin: auto 0;
    line-height: 14px;
  }
  color: var(--v-secondary1-base);
}

.patient-name {
  color: var(--v-secondary-base);
}
</style>
